import React, { useState, useEffect } from 'react';
import api from '../../api';
import { FaCogs, FaSave, FaTrash, FaPlus, FaMinus, FaHome, FaBuilding, FaUsers, FaSolarPanel, FaIndustry } from 'react-icons/fa';
import styles from './Admin.module.css'; // Shared CSS

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  // --- FORM STATE ---
  const [newService, setNewService] = useState({
    title: '',
    description: '',
    iconName: 'FaHome', // Default Icon
    colorHex: '#00D084' // Default Green
  });

  // Features list (Array of strings)
  const [features, setFeatures] = useState(['']); 



  // Available Icons for Dropdown
  const iconOptions = [
    { name: "FaHome", icon: <FaHome /> },
    { name: "FaBuilding", icon: <FaBuilding /> },
    { name: "FaUsers", icon: <FaUsers /> },
    { name: "FaSolarPanel", icon: <FaSolarPanel /> },
    { name: "FaIndustry", icon: <FaIndustry /> }
  ];

  // --- 1. FETCH DATA ---
  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const res = await api.get('/Services');
      setServices(res.data);
    } catch (err) {
      console.error("Error fetching services", err);
    }
  };

  // --- 2. HANDLE FORM INPUTS ---
  const handleInputChange = (e) => {
    setNewService({ ...newService, [e.target.name]: e.target.value });
  };

  // --- 3. DYNAMIC FEATURES LOGIC ---
  const handleFeatureChange = (index, value) => {
    const updatedFeatures = [...features];
    updatedFeatures[index] = value;
    setFeatures(updatedFeatures);
  };

  const addFeatureField = () => {
    setFeatures([...features, '']); // Add empty field
  };

  const removeFeatureField = (index) => {
    const updatedFeatures = features.filter((_, i) => i !== index);
    setFeatures(updatedFeatures);
  };

  // --- 4. SUBMIT FORM (ADD SERVICE) ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });

    // Payload tayar karein (Empty features remove karke)
    const payload = {
      ...newService,
      features: features.filter(f => f.trim() !== '') 
    };

    try {
      const token = localStorage.getItem('token');
   await api.post('/Services', payload);
      setMessage({ text: "Service Added Successfully!", type: "success" });
      fetchServices(); // Refresh List
      
      // Reset Form
      setNewService({ title: '', description: '', iconName: 'FaHome', colorHex: '#00D084' });
      setFeatures(['']);

    } catch (err) {
      setMessage({ text: "Failed to add service.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  // --- 5. DELETE SERVICE ---
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure? This will delete the service and all its features.")) return;

    try {
      const token = localStorage.getItem('token');
   await api.delete(`/Services/${id}`);
      fetchServices();
    } catch (err) {
      alert("Error deleting service");
    }
  };

  return (
    <div>
      <div className={styles.formCard} style={{ maxWidth: '1200px' }}>
        
        <h3 style={{ marginBottom: '1.5rem', color: '#4ade80', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FaCogs /> Manage Services
        </h3>

        {/* Message Alert */}
        {message.text && (
          <div style={{
            padding: '12px', marginBottom: '20px', borderRadius: '8px',
            background: message.type === 'error' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(74, 222, 128, 0.15)',
            color: message.type === 'error' ? '#fca5a5' : '#4ade80'
          }}>
            {message.text}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
          
          {/* LEFT SIDE: ADD NEW SERVICE FORM */}
          <div>
            <h4 style={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '10px', marginBottom: '15px' }}>
              Add New Service
            </h4>
            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label>Service Title</label>
                <input type="text" name="title" value={newService.title} onChange={handleInputChange} className={styles.formInput} required />
              </div>

              <div className={styles.formGroup}>
                <label>Description</label>
                <textarea name="description" value={newService.description} onChange={handleInputChange} className={styles.formInput} rows="3" required />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                {/* Icon Selector */}
                <div className={styles.formGroup}>
                  <label>Select Icon</label>
                  <select name="iconName" value={newService.iconName} onChange={handleInputChange} className={styles.formInput}>
                    {iconOptions.map(opt => (
                      <option key={opt.name} value={opt.name}>{opt.name}</option>
                    ))}
                  </select>
                </div>

                {/* Color Picker */}
                <div className={styles.formGroup}>
                  <label>Theme Color</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input type="color" name="colorHex" value={newService.colorHex} onChange={handleInputChange} style={{ height: '45px', width: '50px', padding: 0, border: 'none', background: 'none', cursor: 'pointer' }} />
                    <span style={{ color: '#fff' }}>{newService.colorHex}</span>
                  </div>
                </div>
              </div>

              {/* Dynamic Features List */}
              <div className={styles.formGroup}>
                <label>Features (Bullet Points)</label>
                {features.map((feat, index) => (
                  <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <input 
                      type="text" 
                      value={feat} 
                      onChange={(e) => handleFeatureChange(index, e.target.value)} 
                      className={styles.formInput} 
                      placeholder={`Feature ${index + 1}`}
                    />
                    {features.length > 1 && (
                      <button type="button" onClick={() => removeFeatureField(index)} style={{ background: 'rgba(239,68,68,0.2)', color: '#ef4444', border: 'none', borderRadius: '5px', padding: '0 12px', cursor: 'pointer' }}>
                        <FaMinus />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addFeatureField} style={{ background: 'none', border: '1px dashed #4ade80', color: '#4ade80', padding: '8px', width: '100%', borderRadius: '5px', cursor: 'pointer' }}>
                  <FaPlus /> Add Another Feature
                </button>
              </div>

              <button type="submit" className={styles.primaryBtn} disabled={loading} style={{ marginTop: '10px' }}>
                {loading ? 'Adding...' : <><FaSave /> Add Service</>}
              </button>
            </form>
          </div>

          {/* RIGHT SIDE: EXISTING SERVICES LIST */}
          <div>
            <h4 style={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '10px', marginBottom: '15px' }}>
              Existing Services ({services.length})
            </h4>
            <div style={{ maxHeight: '600px', overflowY: 'auto', paddingRight: '5px' }}>
              {services.map((service) => (
                <div key={service.id} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '10px', padding: '15px', marginBottom: '15px', borderLeft: `4px solid ${service.colorHex}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h5 style={{ margin: '0 0 5px 0', color: '#fff', fontSize: '1.1rem' }}>
                        {service.title}
                      </h5>
                      <small style={{ color: '#94a3b8', display: 'block', marginBottom: '10px' }}>
                        Icon: {service.iconName}
                      </small>
                    </div>
                    <button onClick={() => handleDelete(service.id)} className={styles.deleteBtn} style={{ width: 'auto', padding: '5px 10px' }}>
                      <FaTrash />
                    </button>
                  </div>
                  
                  <p style={{ color: '#cbd5e1', fontSize: '0.9rem', marginBottom: '10px' }}>
                    {service.description.substring(0, 80)}...
                  </p>

                  <div style={{ background: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '5px' }}>
                    <p style={{ margin: '0 0 5px 0', fontSize: '0.8rem', color: '#4ade80' }}>Features:</p>
                    <ul style={{ margin: 0, paddingLeft: '20px', color: '#94a3b8', fontSize: '0.85rem' }}>
                      {service.features && service.features.map((f, i) => (
                        <li key={i}>{f.featureText}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminServices;